import { formatInr } from "@/utils/basicFunctions.util";
import { useState } from "react";
import type {
  MembershipBenefitOption,
  MembershipKind,
  MembershipWho,
  MembershipWhoOption,
} from "../../../data";

interface UseMembershipBuilderInput {
  benefits: MembershipBenefitOption[];
  who: MembershipWhoOption[];
  initialPrice: number;
  card: {
    free: { kind: string; title: string; cost: string };
    paid: { kind: string; title: string; cost: string };
    emptyBenefits: string;
  };
}

// The membership card is derived entirely from the four choices: free or
// paid, the yearly price, the ticked benefits and who qualifies.
export function useMembershipBuilder({
  benefits,
  who,
  initialPrice,
  card,
}: UseMembershipBuilderInput) {
  const initialState = {
    kind: "free" as MembershipKind,
    // Kept as the raw input text so the field can be cleared while typing.
    price: String(initialPrice),
    selected: benefits.filter((item) => item.isDefault).map((item) => item.key),
    who: "anyone" as MembershipWho,
  };
  const [state, setState] = useState(initialState);

  const setKind = (kind: MembershipKind) =>
    setState((state) => ({ ...state, kind }));
  const setWho = (whoKey: MembershipWho) =>
    setState((state) => ({ ...state, who: whoKey }));
  const setPrice = (price: string) =>
    setState((state) => ({ ...state, price }));
  const toggleBenefit = (key: string) =>
    setState((state) => ({
      ...state,
      selected: state.selected.includes(key)
        ? state.selected.filter((item) => item !== key)
        : [...state.selected, key],
    }));

  const isPaid = state.kind === "paid";
  const cardCopy = isPaid ? card.paid : card.free;
  const cost = isPaid
    ? card.paid.cost.replace("{price}", formatInr(parseFloat(state.price) || 0))
    : card.free.cost;
  // Benefits are listed in the order of the checkboxes, as in the preview.
  const chosen = benefits
    .filter((item) => state.selected.includes(item.key))
    .map((item) => item.label);
  const cardBenefits = chosen.length ? chosen : [card.emptyBenefits];
  const whoText = who.find((item) => item.key === state.who)?.cardText ?? "";

  return {
    kind: state.kind,
    price: state.price,
    selected: state.selected,
    who: state.who,
    isPaid,
    setKind,
    setWho,
    setPrice,
    toggleBenefit,
    cardKind: cardCopy.kind,
    cardTitle: cardCopy.title,
    cost,
    cardBenefits,
    whoText,
  };
}
